const MAX_QUERY_LENGTH = 240;
const MAX_LIST_ITEMS = 12;
export const SHARE_REDIRECT_KEY = 'ss_share_redirect';
export const SIGNUP_FROM_SHARE_KEY = 'ss_signup_from_share';

function cleanText(value, max = MAX_QUERY_LENGTH) {
  if (value === null || value === undefined) return '';
  return String(value).trim().slice(0, max);
}

function cleanList(value) {
  return cleanText(value, 500)
    .split(',')
    .map(item => cleanText(item, 80))
    .filter(Boolean)
    .slice(0, MAX_LIST_ITEMS);
}

function intInRange(value, fallback, min, max) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

export function parseDeepLinkFromLocation(pathname, search = '') {
  const params = new URLSearchParams(search);

  if (pathname === '/application') {
    const daId = params.get('id') || '';
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(daId)
      ? { type: 'da', daId } : null;
  }

  if (pathname === '/da') {
    const appNumber = cleanText(params.get('app'), 50);
    if (!appNumber) return null;
    return {
      type: 'da',
      appNumber,
      council: cleanText(params.get('council'), 100),
    };
  }

  if (pathname === '/' && params.get('da')) {
    return {
      type: 'da',
      appNumber: cleanText(params.get('da'), 50),
      council: cleanText(params.get('council'), 100),
    };
  }

  if (pathname === '/notification') {
    const ids = [...new Set((params.get('ids') || '').split(',')
      .filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)))].slice(0, 40);
    if (!ids.length) return null;
    return { type: 'notification', ids, total: intInRange(params.get('total'), ids.length, ids.length, 10000), kind: params.get('kind') === 'exhibition' ? 'exhibition' : 'status' };
  }

  if (pathname === '/tracked') return { type: 'tracked' };

  if (pathname === '/watch-zone') {
    const zoneId = cleanText(params.get('id') || params.get('zone_id'), 80);
    const name = cleanText(params.get('name'), 100);
    if (!zoneId && !name) return null;
    return { type: 'watch_zone', zoneId, name };
  }

  if (pathname === '/saved-search') {
    const query = cleanText(params.get('q') || params.get('query'));
    const councils = cleanList(params.get('councils'));
    const statuses = cleanList(params.get('statuses'));
    const categories = cleanList(params.get('categories'));
    if (!query && councils.length === 0 && statuses.length === 0 && categories.length === 0) return null;
    return { type: 'saved_search', query, councils, statuses, categories };
  }

  if (pathname === '/patch-summary') {
    return {
      type: 'patch_summary',
      days: intInRange(params.get('days'), 7, 1, 90),
      source: cleanText(params.get('source'), 80),
    };
  }

  return null;
}

export function parseCurrentDeepLink() {
  return parseDeepLinkFromLocation(window.location.pathname, window.location.search);
}

export function isSafeInternalRedirect(value) {
  if (!value || typeof value !== 'string') return false;
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  if (value.includes('://') || value.includes('\\')) return false;
  // eslint-disable-next-line no-control-regex -- Reject control characters in untrusted redirects.
  if (/[\u0000-\u001f\u007f]/.test(value)) return false;
  return true;
}

export function getSafeRedirectFromSearch(search = '') {
  try {
    const value = new URLSearchParams(search).get('redirect') || '';
    return isSafeInternalRedirect(value) ? value : '';
  } catch {
    return '';
  }
}

export function buildAuthUrl(mode = 'signin', redirectPath = '') {
  const params = new URLSearchParams();
  params.set('auth', mode === 'signup' ? 'signup' : 'signin');
  if (isSafeInternalRedirect(redirectPath)) params.set('redirect', redirectPath);
  return `/?${params.toString()}`;
}

export function storeShareRedirect(redirectPath, signupPayload = null) {
  if (!isSafeInternalRedirect(redirectPath)) return false;
  try { sessionStorage.setItem(SHARE_REDIRECT_KEY, redirectPath); } catch { /* ignore */ }
  try { localStorage.setItem(SHARE_REDIRECT_KEY, redirectPath); } catch { /* ignore */ }
  if (signupPayload) {
    const payload = JSON.stringify(signupPayload);
    try { sessionStorage.setItem(SIGNUP_FROM_SHARE_KEY, payload); } catch { /* ignore */ }
    try { localStorage.setItem(SIGNUP_FROM_SHARE_KEY, payload); } catch { /* ignore */ }
  }
  return true;
}

export function consumeShareRedirect(search = window.location.search) {
  const candidates = [
    getSafeRedirectFromSearch(search),
    (() => { try { return sessionStorage.getItem(SHARE_REDIRECT_KEY) || ''; } catch { return ''; } })(),
    (() => { try { return localStorage.getItem(SHARE_REDIRECT_KEY) || ''; } catch { return ''; } })(),
  ];
  try { sessionStorage.removeItem(SHARE_REDIRECT_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(SHARE_REDIRECT_KEY); } catch { /* ignore */ }
  return candidates.find(isSafeInternalRedirect) || '';
}
